import { apiFetch } from "@/apis/http/apiFetch";
import type { Watchlist } from "@/shared/types/watchlist";

const BASE = "/api/v1/watchlists";

export interface WatchlistSecurityItem {
  securityIdentifier: string;
  issuerName: string;
  obligorName: string;
  stateCode: string;
  couponRate: number | null;
  maturityDate: string | null;
  nextCallDate: string | null;
  offerCount: number;
  offerQuantity: number;
  bidCount: number;
  bidQuantity: number;
  quoteRequestCount: number;
  quoteRequestQuantity: number;
  tradeCount: number;
  tradeQuantity: number;
  colorTags: string[];
  useOfProceeds: string;
  latestRedemptionType: string;
  /** Derived Bond Type code; empty until the watchlist indexer has been replayed. */
  bondType?: string;
  features?: string[];
  isDoubleBarreled?: boolean;
}

export interface WatchlistSecuritiesResponse {
  items: WatchlistSecurityItem[];
  totalCount: number;
  offset: number;
  limit: number;
}

interface NumericRange {
  min?: number;
  max?: number;
}

interface DateRange {
  min?: string;
  max?: string;
}

interface IncludeExcludeRequestFilter {
  values: string[];
  exclude: boolean;
}

/**
 * Query params for GET /{id}/securities — mirrors Tower.Api.Contracts.WatchlistSecuritiesFilterRequest.
 * Filtering/sorting happens server-side against the Gold `gold_watchlist_cusips_view` index
 * (see ElasticWatchlistSearchService.SearchCusipsAsync); this hits the same page of data the
 * grid renders, so pagination is real (Offset/Limit), not a client-side slice.
 */
export interface WatchlistSecuritiesFilterRequest {
  coupon?: NumericRange;
  maturityDate?: DateRange;
  nextCallDate?: DateRange;

  states?: string[];
  issuers?: string[]; // CUSIP6 prefixes
  cusips?: string[];
  obligors?: string[]; // contains-match terms

  isFederallyTaxable?: boolean;
  isStateTaxable?: boolean;
  isAmt?: boolean;
  isBankQualified?: boolean;

  bondType?: IncludeExcludeRequestFilter;
  features?: IncludeExcludeRequestFilter;
  isDoubleBarreled?: boolean;
  sectors?: string[];
  rateStructures?: IncludeExcludeRequestFilter;
  redemptionStatuses?: IncludeExcludeRequestFilter;

  isPutable?: boolean;
  isSinkable?: boolean;
  isPreRefunded?: boolean;
  isEscrowed?: boolean;
  isInsured?: boolean;
  isEnhanced?: boolean;
  isGuarantee?: boolean;
  isGreenBond?: boolean;
  attributesExcludedFields?: string[]; // View field names, e.g. "IsPreRefunded"

  moodysMinRating?: string;
  spMinRating?: string;
  ratingsCombine?: string; // "AND" | "OR" — "OR" currently degrades to "AND" server-side
  ratingsUseUnderlyingOnly?: boolean;

  offset?: number;
  limit?: number;
  sortBy?: string;
  sortDescending?: boolean;
  includeMarketData?: boolean;
}

function appendRangeParams(
  query: URLSearchParams,
  prefix: string,
  range?: { min?: number | string; max?: number | string },
): void {
  if (!range) {
    return;
  }

  if (range.min !== undefined && range.min !== null && `${range.min}`.length > 0) {
    query.set(`${prefix}.min`, `${range.min}`);
  }

  if (range.max !== undefined && range.max !== null && `${range.max}`.length > 0) {
    query.set(`${prefix}.max`, `${range.max}`);
  }
}

function appendListParam(query: URLSearchParams, key: string, values?: string[]): void {
  if (!values || values.length === 0) {
    return;
  }
  for (const value of values) {
    query.append(key, value);
  }
}

function appendIncludeExcludeParam(query: URLSearchParams, prefix: string, filter?: IncludeExcludeRequestFilter): void {
  if (!filter || filter.values.length === 0) {
    return;
  }
  appendListParam(query, `${prefix}.values`, filter.values);
  query.set(`${prefix}.exclude`, `${filter.exclude}`);
}

function appendBoolParam(query: URLSearchParams, key: string, value?: boolean): void {
  if (value !== undefined) {
    query.set(key, `${value}`);
  }
}

function appendIdentifierParams(query: URLSearchParams, filters: WatchlistSecuritiesFilterRequest): void {
  appendListParam(query, "states", filters.states);
  appendListParam(query, "issuers", filters.issuers);
  appendListParam(query, "cusips", filters.cusips);
  appendListParam(query, "obligors", filters.obligors);
}

function appendTaxStatusParams(query: URLSearchParams, filters: WatchlistSecuritiesFilterRequest): void {
  appendBoolParam(query, "isFederallyTaxable", filters.isFederallyTaxable);
  appendBoolParam(query, "isStateTaxable", filters.isStateTaxable);
  appendBoolParam(query, "isAmt", filters.isAmt);
  appendBoolParam(query, "isBankQualified", filters.isBankQualified);
}

function appendClassificationParams(query: URLSearchParams, filters: WatchlistSecuritiesFilterRequest): void {
  appendIncludeExcludeParam(query, "bondType", filters.bondType);
  appendIncludeExcludeParam(query, "features", filters.features);
  appendBoolParam(query, "isDoubleBarreled", filters.isDoubleBarreled);
  appendListParam(query, "sectors", filters.sectors);
  appendIncludeExcludeParam(query, "rateStructures", filters.rateStructures);
  appendIncludeExcludeParam(query, "redemptionStatuses", filters.redemptionStatuses);
}

function appendAttributeParams(query: URLSearchParams, filters: WatchlistSecuritiesFilterRequest): void {
  appendBoolParam(query, "isPutable", filters.isPutable);
  appendBoolParam(query, "isSinkable", filters.isSinkable);
  appendBoolParam(query, "isPreRefunded", filters.isPreRefunded);
  appendBoolParam(query, "isEscrowed", filters.isEscrowed);
  appendBoolParam(query, "isInsured", filters.isInsured);
  appendBoolParam(query, "isEnhanced", filters.isEnhanced);
  appendBoolParam(query, "isGuarantee", filters.isGuarantee);
  appendBoolParam(query, "isGreenBond", filters.isGreenBond);
  appendListParam(query, "attributesExcludedFields", filters.attributesExcludedFields);
}

function appendRatingParams(query: URLSearchParams, filters: WatchlistSecuritiesFilterRequest): void {
  if (filters.moodysMinRating) query.set("moodysMinRating", filters.moodysMinRating);
  if (filters.spMinRating) query.set("spMinRating", filters.spMinRating);
  if (filters.ratingsCombine) query.set("ratingsCombine", filters.ratingsCombine);
  appendBoolParam(query, "ratingsUseUnderlyingOnly", filters.ratingsUseUnderlyingOnly);
}

function appendPaginationAndSortParams(query: URLSearchParams, filters: WatchlistSecuritiesFilterRequest): void {
  if (filters.offset !== undefined) query.set("offset", `${filters.offset}`);
  if (filters.limit !== undefined) query.set("limit", `${filters.limit}`);
  if (filters.sortBy) query.set("sortBy", filters.sortBy);
  appendBoolParam(query, "sortDescending", filters.sortDescending);
  appendBoolParam(query, "includeMarketData", filters.includeMarketData);
}

function buildWatchlistSecuritiesQuery(filters: WatchlistSecuritiesFilterRequest): URLSearchParams {
  const query = new URLSearchParams();

  appendRangeParams(query, "coupon", filters.coupon);
  appendRangeParams(query, "maturityDate", filters.maturityDate);
  appendRangeParams(query, "nextCallDate", filters.nextCallDate);
  appendIdentifierParams(query, filters);
  appendTaxStatusParams(query, filters);
  appendClassificationParams(query, filters);
  appendAttributeParams(query, filters);
  appendRatingParams(query, filters);
  appendPaginationAndSortParams(query, filters);

  return query;
}

function hasQueryParams(query: URLSearchParams): boolean {
  // URLSearchParams.size is not universally available in all TS lib targets.
  return query.toString().length > 0;
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  return response.json() as Promise<T>;
}

export async function fetchWatchlists(): Promise<Watchlist[]> {
  const response = await apiFetch(BASE);
  return parseJsonResponse<Watchlist[]>(response);
}

export interface WatchlistCusipIndexItem {
  cusip: string;
  watchlistId: string;
}

export interface WatchlistCusipIndexResponse {
  items: WatchlistCusipIndexItem[];
}

/**
 * Every (cusip, watchlistId) membership pair across all of the caller's watchlists, in one
 * call — backs the cusip-to-watchlists reverse index without an N-call fan-out over
 * fetchWatchlistSecurities per watchlist.
 */
export async function fetchWatchlistCusipsIndex(): Promise<WatchlistCusipIndexResponse> {
  const response = await apiFetch(`${BASE}/cusips-index`);
  return parseJsonResponse<WatchlistCusipIndexResponse>(response);
}

export async function createWatchlist(name: string): Promise<Watchlist> {
  const response = await apiFetch(BASE, {
    method: "POST",
    body: JSON.stringify({ name }),
  });

  return parseJsonResponse<Watchlist>(response);
}

export async function renameWatchlist(id: string, name: string): Promise<Watchlist> {
  const response = await apiFetch(`${BASE}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });

  return parseJsonResponse<Watchlist>(response);
}

export async function deleteWatchlist(id: string): Promise<void> {
  await apiFetch(`${BASE}/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function fetchWatchlistSecurities(
  watchlistId: string,
  filters?: WatchlistSecuritiesFilterRequest,
): Promise<WatchlistSecuritiesResponse> {
  const query = filters ? buildWatchlistSecuritiesQuery(filters) : new URLSearchParams();

  const baseUrl = `${BASE}/${encodeURIComponent(watchlistId)}/securities`;
  const response = await apiFetch(
    hasQueryParams(query) ? `${baseUrl}?${query.toString()}` : baseUrl,
  );
  return parseJsonResponse<WatchlistSecuritiesResponse>(response);
}

export async function addSecuritiesToWatchlist(
  watchlistId: string,
  securityIdentifiers: string[],
): Promise<Watchlist> {
  const response = await apiFetch(`${BASE}/${encodeURIComponent(watchlistId)}/securities`, {
    method: "POST",
    body: JSON.stringify({ securityIdentifiers }),
  });

  return parseJsonResponse<Watchlist>(response);
}

export async function removeSecuritiesFromWatchlist(
  watchlistId: string,
  securityIdentifiers: string[],
): Promise<Watchlist> {
  const response = await apiFetch(`${BASE}/${encodeURIComponent(watchlistId)}/securities`, {
    method: "DELETE",
    body: JSON.stringify({ securityIdentifiers }),
  });

  return parseJsonResponse<Watchlist>(response);
}


